
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { getInternshipSpecificOneDetails, submitWeeklyReport } from '../../api/userApi';
import { 
    RocketLaunchIcon, CalendarIcon, 
    CheckBadgeIcon, LockClosedIcon, 
    ChevronRightIcon, InformationCircleIcon,
    CheckCircleIcon, ClockIcon, TrophyIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const InternshipRoom = () => {
    const { id } = useParams(); 
    const showToast = useToast();
    const navigate = useNavigate();

    // --- State Management ---
    const [internshipData, setInternshipData] = useState(null);
    const [assignments, setAssignments] = useState([]); 
    const [activeWeek, setActiveWeek] = useState(1);
    const [reportText, setReportText] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const hasInitialLoaded = useRef(false);

    const loadInternshipDetails = useCallback(async (forceRefresh = false) => {
        try {
            if (!hasInitialLoaded.current) setLoading(true);
            const response = await getInternshipSpecificOneDetails(id);
            const { data } = response;
            
            setInternshipData(data);
            setAssignments(data.assignments || []);

            if (!hasInitialLoaded.current) {
                setActiveWeek(data.current_week || 1);
                hasInitialLoaded.current = true;
            }
        } catch (err) {
            console.error("Fetch error:", err);
            showToast("Sync Error: Connection to HQ lost.", "error");
        } finally {
            setLoading(false);
        }
    }, [id, showToast]);

    useEffect(() => { loadInternshipDetails(); }, [loadInternshipDetails]);

    useEffect(() => {
        const interval = setInterval(() => { loadInternshipDetails(true); }, 30000); 
        return () => clearInterval(interval);
    }, [loadInternshipDetails]);

    useEffect(() => {
        setReportText("");
    }, [activeWeek]);

    // --- LOGIC: MISSION STATUS CALCULATOR ---
    const getWeekStatus = (weekNum) => {
        if (!internshipData) return { isLocked: true, label: '...' };

        const report = internshipData.reports?.find(r => r.week_number === weekNum);
        const startDate = new Date(internshipData.start_date);
        const today = new Date();
        
        // Time Gate Calculation
        const daysRequired = (weekNum - 1) * 7;
        const unlockDate = new Date(startDate);
        unlockDate.setDate(startDate.getDate() + daysRequired);
         const isTimeLocked = today < unlockDate;
        // const isTimeLocked = false; // FORCE UNLOCK FOR TESTING

        // Sequence Check
        const prevWeekReport = internshipData.reports?.find(r => r.week_number === weekNum - 1);
        const isPrevApproved = weekNum === 1 || prevWeekReport?.status === 'approved';
        // const isPrevApproved = true; // FORCE SEQUENCE UNLOCK FOR TESTING

        const status = report?.status; 

        return {
            isLocked: isTimeLocked || !isPrevApproved,
            isTimeLocked,
            unlockDate: unlockDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
            isApproved: status === 'approved',
            isRejected: status === 'rejected',
            isPending: status === 'pending',
            feedback: report?.admin_feedback,
            isActionable: !isTimeLocked && isPrevApproved && status !== 'approved' && status !== 'pending',
            label: isTimeLocked ? "TIME LOCKED" : 
                   !isPrevApproved ? "WAITING PREV" :
                   status === 'approved' ? "APPROVED" :
                   status === 'pending' ? "UNDER REVIEW" :
                   status === 'rejected' ? "REVISION REQ" : "OPEN"
        };
    };

    const weekStatus = getWeekStatus(activeWeek);
    const totalWeeks = assignments.length;
    const isMissionFinished = internshipData && totalWeeks > 0 && internshipData.current_week > totalWeeks;

    const handleReportSubmit = async (e) => {
        if (e) e.preventDefault();
        if (submitting || !weekStatus.isActionable) return;

        if (reportText.trim().length < 50) {
            return showToast("Report too short! (Min 50 characters)", "error");
        }
        
        setSubmitting(true); 
        try {
            await submitWeeklyReport({
                internship_id: id,
                week_number: activeWeek,
                report_text: reportText 
            });
            showToast(`Week ${activeWeek} progress transmitted!`, "success");
            setReportText(""); 
            await loadInternshipDetails(true); 
        } catch (err) {
            showToast("Transmission failed. Check network.", "error");
        } finally {
            setSubmitting(false); 
        }
    };

    const wordCount = reportText.trim() === "" ? 0 : reportText.trim().split(/\s+/).length;
    const isOverLimit = wordCount > 350;

    if (loading) return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center font-mono text-emerald-500 animate-pulse uppercase tracking-[0.5em]">
            Syncing Terminal...
        </div>
    );

    const currentTask = assignments.find(a => a.week_number === activeWeek) || {
        title: "Mission Update",
        task_details: "System initializing..."
    };

    return (
        <div className="min-h-screen bg-[#020202] text-gray-300 p-4 lg:p-10 font-mono selection:bg-emerald-500/30">
            
            {/* Header Stats */}
            <div className="max-w-7xl mx-auto mb-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#0a0a0a] border border-white/5 p-4 rounded-2xl flex items-center gap-4">
                    <div className="p-3 bg-emerald-500/10 rounded-xl"><RocketLaunchIcon className="h-6 w-6 text-emerald-500"/></div>
                    <div className="overflow-hidden">
                        <p className="text-[10px] text-gray-500 uppercase">Mission</p>
                        <p className="text-sm font-black text-white truncate">{internshipData?.title}</p>
                    </div>
                </div>
                
                <div className="bg-[#0a0a0a] border border-white/5 p-4 rounded-2xl flex items-center gap-4">
                    <div className="p-3 bg-indigo-500/10 rounded-xl"><CalendarIcon className="h-6 w-6 text-indigo-500"/></div>
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase">Progress</p>
                        <p className="text-sm font-black text-white uppercase">
                            {isMissionFinished ? "COMPLETE" : `Week ${internshipData?.current_week} / ${totalWeeks}`}
                        </p>
                    </div>
                </div>

                <div className="bg-[#0a0a0a] border border-white/5 p-4 rounded-2xl flex items-center gap-4">
                    <div className="p-3 bg-white/5 rounded-xl"><CheckBadgeIcon className="h-6 w-6 text-emerald-500"/></div>
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase">Current Window</p>
                        <p className={`text-sm font-black uppercase ${isMissionFinished ? 'text-emerald-400' : 'text-indigo-400'}`}>
                            {isMissionFinished ? 'CERTIFIED' : weekStatus.label}
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* Sidebar */}
                <div className="lg:col-span-4 space-y-4">
                    <div className="bg-[#0a0a0a] border border-white/5 p-6 rounded-[2rem] shadow-2xl">
                        <h3 className="text-xs font-black text-gray-500 mb-6 uppercase tracking-widest flex items-center gap-2">
                            <InformationCircleIcon className="h-4 w-4"/> Sequence Tracker
                        </h3>
                        <div className="space-y-3">
                            {assignments.map((task) => {
                                const s = getWeekStatus(task.week_number);
                                return (
                                    <button 
                                        key={task.week_number}
                                        onClick={() => setActiveWeek(task.week_number)}
                                        className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all 
                                            ${activeWeek === task.week_number ? 'border-emerald-500 bg-emerald-500/5' : 'border-white/5 hover:bg-white/5'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            {s.isApproved ? <CheckCircleIcon className="h-5 w-5 text-emerald-500" /> 
                                            : s.isPending ? <ClockIcon className="h-5 w-5 text-indigo-500" />
                                            : s.isLocked ? <LockClosedIcon className="h-4 w-4 text-gray-600"/>
                                            : <span className="text-xs font-bold text-white">W0{task.week_number}</span>}
                                            <span className={`text-[10px] font-black uppercase ${s.isApproved ? 'text-emerald-500' : 'text-gray-500'}`}>
                                                {s.label}
                                            </span>
                                        </div>
                                        <ChevronRightIcon className={`h-4 w-4 ${activeWeek === task.week_number ? 'text-emerald-500' : 'text-gray-700'}`} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Main Workspace */}
                <div className="lg:col-span-8 space-y-6">
                    {isMissionFinished ? (
                        <div className="bg-[#0a0a0a] border border-emerald-500/30 rounded-[2.5rem] p-8 md:p-12 shadow-2xl text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500/20" />
                            <TrophyIcon className="h-16 w-16 text-emerald-500 mx-auto mb-6" />
                            <h2 className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.4em] mb-2">Internship Completion</h2>
                            <h3 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase italic">Congratulations!</h3>
                            <p className="text-gray-500 text-sm max-w-md mx-auto mb-8 leading-relaxed font-medium">
                                   You have successfully completed the <span className="text-white">{internshipData?.title}</span> internship program. Your dedication is truly appreciated, and we wish you great success in your future career.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                                <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-black uppercase rounded-xl transition-all" onClick={() => navigate("/dashboard/certificates")}>
                                    <ArrowDownTrayIcon className="h-4 w-4" /> Get Certificate
                                </button>
                                <button className="w-full sm:w-auto px-8 py-3.5 bg-white/5 border border-white/10 text-white text-[10px] font-black uppercase rounded-xl" onClick={() => navigate("/dashboard")}>
                                    Exit Terminal
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Objective Header */}
                            <div className="bg-[#0a0a0a] border border-white/10 rounded-[2rem] p-8 shadow-2xl relative">
                                <h2 className="text-xs font-black text-indigo-400 uppercase tracking-[0.3em] mb-2">Segment 0{activeWeek} Objective</h2>
                                <h3 className="text-xl font-bold text-white mb-4 italic uppercase">{currentTask.title}</h3>
                                <div className="space-y-3">
                                    {currentTask.task_details?.split('\n').map((point, index) => (
                                        point.trim() && (
                                            <div key={index} className="flex items-start gap-3">
                                                <div className="mt-1.5 h-1 w-1 rounded-full bg-emerald-500 shrink-0" />
                                                <p className="text-xs text-gray-400 leading-relaxed">{point}</p>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>

                            {/* Submission Area */}
                            <div className="bg-[#0a0a0a] border border-white/5 rounded-[2rem] p-8 md:p-10 shadow-2xl relative overflow-hidden">
                                
                                {/* OVERLAY: TIME LOCKED */}
                                {weekStatus.isTimeLocked && !weekStatus.isApproved && (
                                    <div className="absolute inset-0 z-50 bg-[#020202]/80 backdrop-blur-md flex flex-col items-center justify-center text-center p-8">
                                        <ClockIcon className="h-12 w-12 text-amber-500 mb-4 animate-pulse" />
                                        <h3 className="text-white font-black uppercase tracking-widest">Temporal Lock</h3>
                                        <p className="text-gray-400 text-xs mt-2 max-w-xs leading-relaxed">
                                            This segment unlocks on <span className="text-amber-500 font-bold">{weekStatus.unlockDate}</span>.
                                        </p>
                                        <p className="text-[10px] text-gray-600 mt-4 uppercase italic">Prepare your report in the meantime.</p>
                                    </div>
                                )}

                                {/* OVERLAY: PENDING REVIEW */}
                                {weekStatus.isPending && (
                                    <div className="absolute inset-0 z-50 bg-[#020202]/90 backdrop-blur-sm flex flex-col items-center justify-center text-center p-8">
                                        <h3 className="text-sm font-black text-white uppercase tracking-widest">Under Review</h3>
                                        <p className="text-gray-500 text-[10px] mt-2 italic">We are reviewing your assignment.</p>
                                    </div>
                                )}

                                {/* VIEW: APPROVED (Archive Mode) */}
                                {weekStatus.isApproved ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                                            <CheckCircleIcon className="h-6 w-6 text-emerald-500" />
                                            <div>
                                                <p className="text-white text-[10px] font-black uppercase">Weekly Assignment Approved</p>
                                                <p className="text-gray-500 text-[10px]">Great work on completing this week's task.</p>
                                            </div>
                                        </div>
                                        <div className="p-6 bg-black/40 border border-white/5 rounded-xl text-xs text-gray-400 italic font-mono leading-relaxed max-h-[300px] overflow-y-auto">
                                            "{weekStatus.feedback || "Keep it up!"}"
                                        </div>
                                    </div>
                                ) : (
                                    /* INPUT: ACTIONABLE FORM */
                                    <form onSubmit={handleReportSubmit} className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Transmission Log</label>
                                            <span className={`text-[10px] font-mono ${isOverLimit ? 'text-red-500' : 'text-emerald-500/50'}`}>
                                                {wordCount} / 350 WORDS
                                            </span>
                                        </div>
                                        <textarea 
                                            className={`w-full h-[200px] bg-black border rounded-xl p-6 text-xs text-gray-400 outline-none transition-all font-mono
                                                ${isOverLimit ? 'border-red-500' : 'border-white/10 focus:border-emerald-500'}`}
                                            placeholder="> INITIALIZE REPORT..."
                                            value={reportText}
                                            onChange={(e) => setReportText(e.target.value)}
                                            disabled={!weekStatus.isActionable || submitting}
                                        />
                                        <button 
                                            type="submit"
                                            disabled={submitting || !weekStatus.isActionable || isOverLimit || reportText.trim().length < 50}
                                            className="w-full py-4 bg-emerald-600 disabled:bg-gray-800 disabled:text-gray-600 hover:bg-emerald-500 text-white text-[10px] font-black uppercase italic tracking-[0.3em] rounded-xl transition-all shadow-xl"
                                        >
                                            {submitting ? "SENDING..." : "Finalize Segment"}
                                        </button>
                                    </form>
                                )}
                            </div>

                            {/* REJECTION FEEDBACK */}
                            {weekStatus.isRejected && (
                                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/50 rounded-2xl">
                                    <div className="flex items-center gap-2 text-red-500 mb-2">
                                        <InformationCircleIcon className="h-5 w-5" />
                                        <span className="text-[10px] font-black uppercase">Revision Required</span>
                                    </div>
                                    <p className="text-xs text-gray-300 italic">"{weekStatus.feedback}"</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InternshipRoom;